import type { BusinessPartner, Address, SalesPerson } from '@/api/business-partner/business-partner.types';
import { DsKv } from '@/modules/internal-v2/ds';

interface BpOverviewTabProps {
  bp: BusinessPartner;
  addresses: Address[];
  salesPerson: SalesPerson | null | undefined;
}

export function BpOverviewTab({ bp, addresses, salesPerson }: BpOverviewTabProps) {
  return (
    <div className="bp-overview ds-scroll">
      <DetailsSection bp={bp} salesPerson={salesPerson} />
      <AddressesSection addresses={addresses} bp={bp} />
    </div>
  );
}

function DetailsSection({
  bp,
  salesPerson,
}: {
  bp: BusinessPartner;
  salesPerson: SalesPerson | null | undefined;
}) {
  return (
    <div className="bp-ov-section">
      <div className="bp-ov-section-label">Details</div>
      <DsKv
        keyWidth={120}
        entries={[
          { label: 'USt-IdNr.', value: bp.vatId || null, missing: !bp.vatId },
          {
            label: 'Preisliste',
            value: (
              <span style={{ fontFamily: 'var(--font-mono)' }}>
                {bp.defaultPriceListId}
              </span>
            ),
          },
          {
            label: 'Vertrieb',
            value: salesPerson
              ? `${salesPerson.firstName} ${salesPerson.lastName}`
              : null,
            missing: !salesPerson,
          },
          ...(salesPerson?.email
            ? [
                {
                  label: 'V-E-Mail',
                  value: (
                    <a
                      href={`mailto:${salesPerson.email}`}
                      style={{ color: 'var(--accent-ink)' }}
                    >
                      {salesPerson.email}
                    </a>
                  ),
                },
              ]
            : []),
          ...(salesPerson?.officePhone || salesPerson?.mobilePhone
            ? [
                {
                  label: 'V-Telefon',
                  value: (
                    <span style={{ fontFamily: 'var(--font-mono)' }}>
                      {salesPerson.officePhone || salesPerson.mobilePhone}
                    </span>
                  ),
                },
              ]
            : []),
        ]}
      />
    </div>
  );
}

function AddressesSection({
  addresses,
  bp,
}: {
  addresses: Address[];
  bp: BusinessPartner;
}) {
  if (addresses.length === 0) {
    return (
      <div className="bp-ov-section">
        <div className="bp-ov-section-label">Adressen</div>
        <div
          style={{
            color: 'var(--ink-3)',
            fontSize: 'var(--fs-xs)',
            padding: '4px 0',
          }}
        >
          Keine Adressen hinterlegt.
        </div>
      </div>
    );
  }

  const billAddresses = addresses.filter((a) => a.addressType === 'bo_BillTo');
  const shipAddresses = addresses.filter((a) => a.addressType === 'bo_ShipTo');
  const sorted = [...billAddresses, ...shipAddresses];

  return (
    <div className="bp-ov-section">
      <div className="bp-ov-section-label">
        Adressen <span className="bp-ov-count">{addresses.length}</span>
      </div>
      <div className="bp-ov-addr-list">
        {sorted.map((addr) => {
          const isShip = addr.addressType === 'bo_ShipTo';
          const isDefault =
            (isShip && addr.id === bp.defaultShippingAddressId) ||
            (!isShip && addr.id === bp.defaultBillingAddressId);
          const mapsQuery = [addr.street, addr.zipCode, addr.city, addr.country]
            .filter(Boolean)
            .join(', ');
          const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsQuery)}`;

          return (
            <div key={`${addr.addressType}-${addr.id}`} className="bp-addr-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span className={`addr-type ${isShip ? 'ship' : 'bill'}`}>
                  {isShip ? 'Liefer' : 'Rechnung'}
                </span>
                {isDefault && <span className="addr-default">Standard</span>}
              </div>
              <div className="addr-name">
                {addr.addressName2 || addr.addressName || '—'}
              </div>
              {addr.street && <div className="addr-line">{addr.street}</div>}
              <div className="addr-line">
                {[addr.zipCode, addr.city].filter(Boolean).join(' ')}
                {addr.country && (
                  <span
                    style={{
                      marginLeft: 6,
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10,
                      color: 'var(--ink-3)',
                    }}
                  >
                    {addr.country}
                  </span>
                )}
              </div>
              {mapsQuery && (
                <span
                  className="addr-maps"
                  onClick={() => window.open(mapsUrl, '_blank')}
                >
                  Maps →
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
